import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  ChatService,
  ChatSession,
  ChatMessage,
  MessageRole,
  SessionPagination,
  MessagePagination,
  AxiosErrorResponse,
} from "@/lib/api/services/chatService";

const LAST_MESSAGE_PREVIEW_LENGTH = 255;

export interface ChatState {
  sessions: ChatSession[];
  sessionPagination: SessionPagination | null;
  activeSession: ChatSession | null;
  activeSiteId: number | null;
  activeOrganizationId: number | null;
  messages: ChatMessage[];
  messagePagination: MessagePagination | null;
  isSending: boolean;
  pendingUserMessage: string | null;
  isLoadingSessions: boolean;
  isLoadingMessages: boolean;
  isCreatingSession: boolean;
  isDeletingSession: boolean;
  isSidebarOpen: boolean;
  error: string | null;
  lastSuccessfulMessageFetch: number | null;
}

const initialState: ChatState = {
  sessions: [],
  sessionPagination: null,
  activeSession: null,
  activeSiteId: null,
  activeOrganizationId: null,
  messages: [],
  messagePagination: null,
  isSending: false,
  pendingUserMessage: null,
  isLoadingSessions: false,
  isLoadingMessages: false,
  isCreatingSession: false,
  isDeletingSession: false,
  isSidebarOpen: false,
  error: null,
  lastSuccessfulMessageFetch: null,
};

const handleApiError = (error: unknown): string => {
  const axiosError = error as AxiosErrorResponse;
  const data = axiosError.response?.data;
  if (data)
    return Array.isArray(data.message)
      ? data.message[0]
      : data.message || "An unexpected error occurred";
  return axiosError.message || "An unexpected error occurred";
};

export interface GetSessionsParams {
  organizationId?: number;
  siteId?: number;
  page?: number;
  limit?: number;
  search?: string;
}
export interface CreateSessionParams {
  organizationId?: number;
  siteId?: number;
  title?: string;
}
export interface GetMessagesParams {
  sessionId: number;
  page?: number;
  limit?: number;
  organizationId?: number;
  siteId?: number;
}
export interface SendMessageParams {
  sessionId: number;
  content: string;
  organizationId?: number;
  siteId?: number;
}

export const getSessions = createAsyncThunk(
  "chat/getSessions",
  async (params: GetSessionsParams, { rejectWithValue }) => {
    try {
      return await ChatService.getSessions(params);
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

export const createSession = createAsyncThunk(
  "chat/createSession",
  async (params: CreateSessionParams, { rejectWithValue }) => {
    try {
      return await ChatService.createSession(params);
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

export const deleteSession = createAsyncThunk(
  "chat/deleteSession",
  async ({ sessionId }: { sessionId: number }, { rejectWithValue }) => {
    try {
      await ChatService.deleteSession(sessionId);
      return sessionId;
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

export const updateSession = createAsyncThunk(
  "chat/updateSession",
  async (
    { sessionId, title }: { sessionId: number; title: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await ChatService.updateSession(sessionId, title);
      return response.session;
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

export const getMessages = createAsyncThunk(
  "chat/getMessages",
  async (
    { sessionId, page, limit, organizationId, siteId }: GetMessagesParams,
    { rejectWithValue },
  ) => {
    try {
      return await ChatService.getMessages(sessionId, {
        page,
        limit,
        organizationId,
        siteId,
      });
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

export const sendMessage = createAsyncThunk(
  "chat/sendMessage",
  async (
    { sessionId, content, siteId, organizationId }: SendMessageParams,
    { rejectWithValue },
  ) => {
    try {
      return await ChatService.sendMessage(sessionId, {
        content,
        ...(siteId !== undefined && { siteId }),
        ...(organizationId !== undefined && { organizationId }),
      });
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

const chatSlice = createSlice({
  name: "chat",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    resetChat: () => initialState,

    setActiveSession: (state, action: PayloadAction<ChatSession | null>) => {
      state.activeSession = action.payload;
      state.messages = [];
      state.messagePagination = null;
      state.lastSuccessfulMessageFetch = null;
    },

    setChatContext: (
      state,
      action: PayloadAction<{
        organizationId: number | null;
        siteId: number | null;
      }>,
    ) => {
      const { organizationId, siteId } = action.payload;

      const orgChanged = state.activeOrganizationId !== organizationId;
      const siteChanged = state.activeSiteId !== siteId;

      if (!orgChanged && !siteChanged) {
        return;
      }

      state.activeOrganizationId = organizationId;
      state.activeSiteId = siteId;

      // Clear sessions and pagination when context changes
      state.sessions = [];
      state.sessionPagination = null;

      // Always clear activeSession when organization changes or site changes
      if (orgChanged || siteChanged) {
        state.activeSession = null;
        state.messages = [];
        state.messagePagination = null;
        state.lastSuccessfulMessageFetch = null;
        state.isSending = false;
        state.pendingUserMessage = null;
        state.error = null;
        state.isLoadingSessions = false;
        state.isLoadingMessages = false;
        state.isCreatingSession = false;
        state.isDeletingSession = false;
        state.isSidebarOpen = false;
      }
    },

    setActiveSiteId: (state, action: PayloadAction<number | null>) => {
      const newSiteId = action.payload;
      if (state.activeSiteId === newSiteId) return;
      state.activeSiteId = newSiteId;
      state.sessions = [];
      state.sessionPagination = null;

      if (state.activeSession && state.activeSession.siteId !== newSiteId) {
        state.activeSession = null;
        state.messages = [];
        state.messagePagination = null;
        state.lastSuccessfulMessageFetch = null;
      }
    },

    toggleSidebar: (state) => {
      state.isSidebarOpen = !state.isSidebarOpen;
    },

    setSidebarOpen: (state, action: PayloadAction<boolean>) => {
      state.isSidebarOpen = action.payload;
    },

    setPendingUserMessage: (state, action: PayloadAction<string | null>) => {
      state.pendingUserMessage = action.payload;
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(getSessions.pending, (state) => {
        state.isLoadingSessions = true;
        state.error = null;
      })
      .addCase(getSessions.fulfilled, (state, action) => {
        state.isLoadingSessions = false;

        // Prevent race condition: only update if the context still matches
        const { organizationId, siteId } = action.meta.arg;
        if (
          state.activeOrganizationId !== (organizationId ?? null) ||
          state.activeSiteId !== (siteId ?? null)
        ) {
          return;
        }

        state.sessions = action.payload.sessions;
        state.sessionPagination = action.payload.pagination;
      })
      .addCase(getSessions.rejected, (state, action) => {
        state.isLoadingSessions = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(createSession.pending, (state) => {
        state.isCreatingSession = true;
        state.error = null;
      })
      .addCase(createSession.fulfilled, (state, action) => {
        state.isCreatingSession = false;

        // Prevent race condition: only update if the context still matches
        const { organizationId, siteId } = action.meta.arg;
        if (
          state.activeOrganizationId !== (organizationId ?? null) ||
          state.activeSiteId !== (siteId ?? null)
        ) {
          return;
        }

        const newSession = action.payload.session;
        state.sessions = [
          newSession,
          ...state.sessions.filter((s) => s.id !== newSession.id),
        ];
        state.activeSession = newSession;
        state.messages = [];
        state.messagePagination = null;
        state.lastSuccessfulMessageFetch = null;
      })
      .addCase(createSession.rejected, (state, action) => {
        state.isCreatingSession = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(deleteSession.pending, (state) => {
        state.isDeletingSession = true;
        state.error = null;
      })
      .addCase(deleteSession.fulfilled, (state, action) => {
        state.isDeletingSession = false;
        state.sessions = state.sessions.filter((s) => s.id !== action.payload);
        if (state.activeSession?.id === action.payload) {
          state.activeSession = null;
          state.messages = [];
          state.messagePagination = null;
          state.lastSuccessfulMessageFetch = null;
        }
      })
      .addCase(deleteSession.rejected, (state, action) => {
        state.isDeletingSession = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(updateSession.fulfilled, (state, action) => {
        const updated = action.payload;
        state.sessions = state.sessions.map((s) =>
          s.id === updated.id ? { ...s, title: updated.title } : s,
        );
        if (state.activeSession?.id === updated.id) {
          state.activeSession = {
            ...state.activeSession,
            title: updated.title,
          };
        }
      })
      .addCase(updateSession.rejected, (state, action) => {
        state.error = action.payload as string;
      });

    builder
      .addCase(getMessages.pending, (state) => {
        state.isLoadingMessages = true;
        state.error = null;
      })
      .addCase(getMessages.fulfilled, (state, action) => {
        state.isLoadingMessages = false;
        const now = Date.now();

        // Prevent race condition: only update if the context still matches
        const { organizationId, siteId, sessionId } = action.meta.arg;
        if (
          state.activeOrganizationId !== (organizationId ?? null) ||
          state.activeSiteId !== (siteId ?? null) ||
          state.activeSession?.id !== sessionId
        ) {
          return;
        }

        if (
          state.lastSuccessfulMessageFetch &&
          now - state.lastSuccessfulMessageFetch < 500
        ) {
          return;
        }

        state.messages = action.payload.messages;
        state.messagePagination = action.payload.pagination;
        state.lastSuccessfulMessageFetch = now;
      })
      .addCase(getMessages.rejected, (state, action) => {
        state.isLoadingMessages = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(sendMessage.pending, (state, action) => {
        state.isSending = true;
        state.error = null;
        state.pendingUserMessage = action.meta.arg.content;
      })
      .addCase(sendMessage.fulfilled, (state, action) => {
        state.isSending = false;
        state.pendingUserMessage = null;

        const { sessionId, content } = action.meta.arg;
        const serverUserMessage = action.payload.userMessage;
        const serverAssistantMessage =
          action.payload.assistantMessage ?? action.payload.data;

        if (serverUserMessage) {
          if (!state.messages.some((m) => m.id === serverUserMessage.id)) {
            state.messages.push(serverUserMessage);
          }
        } else {
          // Fallback when backend only returns assistant message
          // Only push if a user message with the same content doesn't already exist in the list
          const alreadyExists = state.messages.some(
            (m) => m.role === MessageRole.USER && m.content === content,
          );

          if (!alreadyExists) {
            state.messages.push({
              id: -Date.now(),
              sessionId,
              role: MessageRole.USER,
              content,
              createdAt: new Date().toISOString(),
            });
          }
        }

        if (
          serverAssistantMessage &&
          !state.messages.some((m) => m.id === serverAssistantMessage.id)
        ) {
          state.messages.push(serverAssistantMessage);
        }

        const lastAssistantMessage = [...state.messages]
          .reverse()
          .find(
            (m) =>
              m.role === MessageRole.ASSISTANT && m.sessionId === sessionId,
          );
        if (!lastAssistantMessage) return;

        const preview = lastAssistantMessage.content.substring(
          0,
          LAST_MESSAGE_PREVIEW_LENGTH,
        );
        const updatedAt = new Date().toISOString();

        const session = state.sessions.find((s) => s.id === sessionId);
        if (session) {
          session.lastMessage = preview;
          session.updatedAt = updatedAt;
        }
        if (state.activeSession?.id === sessionId) {
          state.activeSession.lastMessage = preview;
          state.activeSession.updatedAt = updatedAt;
        }
      })
      .addCase(sendMessage.rejected, (state, action) => {
        state.isSending = false;
        state.pendingUserMessage = null;
        state.error = action.payload as string;
      });
  },
});

export const {
  clearError,
  resetChat,
  setActiveSession,
  setChatContext,
  setActiveSiteId,
  toggleSidebar,
  setSidebarOpen,
  setPendingUserMessage,
} = chatSlice.actions;
export default chatSlice.reducer;
